import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import { selectUserData } from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import {
  FETCHER_CAPABILITY,
  FETCHER_VIEW_PERMISSION,
} from "../helpers/fetcherPermissions";
import { rowId, selectCatalogueRows } from "../helpers/fetcherCatalogue";
import { SortDirection } from "../helpers/tableSorting";
import {
  readPageParam,
  readRowsPerPageParam,
  readSearchParam,
  writePageParam,
  writeRowsPerPageParam,
  writeSearchParam,
} from "../helpers/urlParams";
import DeleteCatalogueItemModal from "../DeleteCatalogueItemModal";
import { FETCHER_TAB_KEYS } from "../FetcherSettingsTabs";
import InterfaceBrandsModal from "./InterfaceBrandsModal";
import InterfacesFilterCard from "./InterfacesFilterCard";
import InterfacesSkeleton from "./InterfacesSkeleton";
import InterfacesTable, { InterfaceTableRow } from "./InterfacesTable";
import CreateInterfaceModal, {
  InterfaceDraftSeed,
} from "./CreateInterfaceModal";
import EditInterfaceModal from "./EditInterfaceModal";
import ViewInterfaceModal from "./ViewInterfaceModal";

const DEFAULT_SORT_KEY = "id";
const DEFAULT_SORT_DIRECTION: SortDirection = "desc";

const Interfaces = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userData = useSelector(selectUserData);
  const canEdit = CheckPermissions(
    userData?.data?.products?.data_collection,
    FETCHER_CAPABILITY,
    FETCHER_VIEW_PERMISSION,
  );
  const { fetcherSettingsMainTablesData, isCatalogueLoading } = useSelector(
    (state: AppState) => state.fetcherSettings,
  );
  const interfaceRows = useMemo(
    () => selectCatalogueRows(fetcherSettingsMainTablesData, "interface"),
    [fetcherSettingsMainTablesData],
  );
  const selectedName = readSearchParam(searchParams.get("search"));
  const page = readPageParam(searchParams.get("page"));
  const rowsPerPage = readRowsPerPageParam(searchParams.get("page_size"));
  const [sortKey, setSortKey] = useState(DEFAULT_SORT_KEY);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    DEFAULT_SORT_DIRECTION,
  );
  const [brandsModalId, setBrandsModalId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [createSeed, setCreateSeed] = useState<InterfaceDraftSeed | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editing, setEditing] = useState<{
    id: string;
    mode: "view" | "edit";
  } | null>(null);

  // interface names are unique, but the options are deduped for consistency
  const searchOptions = useMemo(
    () =>
      [
        ...new Set(
          interfaceRows
            .map((row) => row.item_name)
            .filter((name): name is string => !!name),
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [interfaceRows],
  );

  const rows: InterfaceTableRow[] = useMemo(
    () =>
      interfaceRows
        .filter((row) => !selectedName || row.item_name === selectedName)
        .map((row) => ({
          id: rowId(row.item_id),
          name: row.item_name ?? "",
          dynamicVar: row.common_dynamic_var ?? "",
          reports: row.child_reports ?? 0,
          configs: row.child_configs ?? 0,
          brands: row.used_by_brands ?? 0,
          objects: row.own_objects ?? 0,
        })),
    [interfaceRows, selectedName],
  );

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
    writePageParam(setSearchParams, 0);
  };

  const openDuplicate = (id: string) => {
    const backendInterface = interfaceRows.find(
      (row) => rowId(row.item_id) === id,
    );
    if (!backendInterface) return;
    setEditing(null);
    setCreateSeed({
      name: `${backendInterface.item_name ?? ""}_copy`,
      dvar: backendInterface.common_dynamic_var ?? "",
      objects: [],
    });
    setIsCreateOpen(true);
  };

  const openReport = (reportId: string) => {
    const report = selectCatalogueRows(
      fetcherSettingsMainTablesData,
      "report",
    ).find((row) => rowId(row.item_id) === reportId);
    if (!report?.item_name) return;
    setEditing(null);
    setSearchParams(
      () =>
        new URLSearchParams({
          tab: FETCHER_TAB_KEYS[1],
          search: report.item_name as string,
        }),
      { replace: true },
    );
  };

  return (
    <>
      <InterfacesFilterCard
        searchOptions={searchOptions}
        selectedName={selectedName}
        onSelectName={(value) => writeSearchParam(setSearchParams, value)}
        canCreate={canEdit}
        onCreate={() => {
          setCreateSeed(null);
          setIsCreateOpen(true);
        }}
      />

      {isCatalogueLoading ? (
        <InterfacesSkeleton />
      ) : (
        <InterfacesTable
          rows={rows}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(value) => writePageParam(setSearchParams, value)}
          onRowsPerPageChange={(value) =>
            writeRowsPerPageParam(setSearchParams, value)
          }
          canEdit={canEdit}
          onView={(id) => setEditing({ id, mode: "view" })}
          onEdit={(id) => setEditing({ id, mode: canEdit ? "edit" : "view" })}
          onDuplicate={openDuplicate}
          onDelete={setDeleteId}
          onShowBrands={setBrandsModalId}
        />
      )}

      <CreateInterfaceModal
        open={isCreateOpen}
        initial={createSeed}
        onClose={() => setIsCreateOpen(false)}
      />

      {editing?.mode === "view" && (
        <ViewInterfaceModal
          open
          interfaceId={editing.id}
          onClose={() => setEditing(null)}
          onSwitchToEdit={() =>
            setEditing((previous) =>
              previous ? { ...previous, mode: "edit" } : previous,
            )
          }
          onDuplicate={openDuplicate}
          onShowBrands={setBrandsModalId}
          onOpenReport={openReport}
          canEdit={canEdit}
        />
      )}

      {editing?.mode === "edit" && (
        <EditInterfaceModal
          open
          interfaceId={editing.id}
          mode="edit"
          onClose={() => setEditing(null)}
          onSwitchToEdit={() =>
            setEditing((previous) =>
              previous ? { ...previous, mode: "edit" } : previous,
            )
          }
          onDuplicate={openDuplicate}
          onShowBrands={setBrandsModalId}
          onOpenReport={openReport}
        />
      )}

      {brandsModalId && (
        <InterfaceBrandsModal
          open
          interfaceId={brandsModalId}
          onClose={() => setBrandsModalId(null)}
        />
      )}

      {deleteId && (
        <DeleteCatalogueItemModal
          open
          level="interface"
          itemId={deleteId}
          onClose={() => setDeleteId(null)}
          onShowConfigs={(reportId) =>
            // the Configs tab reads its filters out of `configFilters`, in the
            // `key=value` shape FilterComponent writes and parses
            setSearchParams(
              () =>
                new URLSearchParams({
                  tab: FETCHER_TAB_KEYS[0],
                  configFilters: `report=${reportId}`,
                }),
              { replace: true },
            )
          }
        />
      )}
    </>
  );
};

export default Interfaces;
